import { useRef, useState } from 'react';
import { displayDAAddress } from '../lib/daDisplay';

const PIPELINE_COLORS = {
  watching:  { bg: "#1a2840", text: "#a8b8cc", dot: "#a8b8cc" },
  contacted: { bg: "#1e3a5f", text: "#3b82f6", dot: "#3b82f6" },
  quoted:    { bg: "#2a2418", text: "#d4a84a", dot: "#d4a84a" },
  won:       { bg: "#142e20", text: "#5aba6a", dot: "#5aba6a" },
  lost:      { bg: "#2e1a20", text: "#d06868", dot: "#d06868" },
  not_relevant: { bg: "#242b36", text: "#8ea0b8", dot: "#8ea0b8" },
};

const STAGES = ['watching', 'contacted', 'quoted'];
const STAGE_LABELS = { watching: 'Watching', contacted: 'Contacted', quoted: 'Quoted', won: 'Won', lost: 'Lost', not_relevant: 'Not relevant' };

function formatCost(n) {
  if (!n) return null;
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${Number(n).toLocaleString()}`;
}

function daysAgo(iso) {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
  if (d === 0) return 'today';
  if (d === 1) return '1 day ago';
  return `${d} days ago`;
}

function formatFollowUp(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(`${dateStr}T00:00:00`);
  const days = Math.round((date - today) / 86400000);
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'due today';
  if (days === 1) return 'due tomorrow';
  return `due in ${days}d`;
}

export default function PipelineStageSelector({ app, updateStage }) {
  const stage         = app.pipeline_stage     || 'watching';
  const trackedId     = app.tracked_id;
  const updatedAt     = app.pipeline_updated_at;
  const quotedAmount  = app.quoted_amount;
  const contactName   = app.contact_name;
  const contactMethod = app.contact_method;
  const wonAmount     = app.won_amount;
  const lostReason    = app.lost_reason;
  const followUpAt    = app.next_follow_up_at;
  const followUpReason = app.follow_up_reason;

  const [sheet, setSheet]           = useState(null);
  const [resetSheet, setResetSheet] = useState(false);
  const [fields, setFields]         = useState({});
  const [saving, setSaving]         = useState(false);
  const followUpDateRef             = useRef(null);

  const openSheet = (target) => {
    if (target === stage) {
      // Tapping the active stage: offer to reset (unless already at watching)
      if (stage !== 'watching') setResetSheet(true);
      return;
    }
    // Pre-fill existing values
    setFields({
      contact_name:   contactName   || '',
      contact_method: contactMethod || '',
      quoted_amount:  quotedAmount  || '',
      won_amount:     wonAmount     || '',
      lost_reason:    lostReason    || '',
      next_follow_up_at: followUpAt || '',
      follow_up_reason:  followUpReason || '',
    });
    setSheet({ target });
  };

  const confirmStage = async () => {
    if (!sheet || saving) return;
    setSaving(true);
    const extra = {};
    if (sheet.target === 'contacted') {
      if (fields.contact_name?.trim())   extra.contact_name   = fields.contact_name.trim();
      if (fields.contact_method?.trim()) extra.contact_method = fields.contact_method.trim();
    }
    if (sheet.target === 'quoted') {
      const amt = parseFloat(fields.quoted_amount);
      if (!isNaN(amt) && amt > 0) extra.quoted_amount = amt;
    }
    if (sheet.target === 'won') {
      const amt = parseFloat(fields.won_amount);
      if (!isNaN(amt) && amt > 0) extra.won_amount = amt;
    }
    if (sheet.target === 'lost') {
      if (fields.lost_reason?.trim()) extra.lost_reason = fields.lost_reason.trim();
    }
    if (sheet.target === 'not_relevant') {
      extra.lost_reason = null;
    }
    if (['won', 'lost', 'not_relevant'].includes(sheet.target)) {
      extra.next_follow_up_at = null;
      extra.follow_up_reason = null;
    } else {
      extra.next_follow_up_at = followUpDateRef.current?.value || fields.next_follow_up_at || null;
      extra.follow_up_reason = fields.follow_up_reason?.trim() || null;
    }
    await updateStage(trackedId, stage, sheet.target, extra);
    setSaving(false);
    setSheet(null);
  };

  const confirmReset = async () => {
    if (saving) return;
    setSaving(true);
    await updateStage(trackedId, stage, 'watching', {
      quoted_amount:  null,
      won_amount:     null,
      contact_name:   null,
      contact_method: null,
      lost_reason:    null,
      next_follow_up_at: null,
      follow_up_reason:  null,
      last_contacted_at: null,
    });
    setSaving(false);
    setResetSheet(false);
  };

  const stageIdx   = STAGES.indexOf(stage);
  const isTerminal = ['won', 'lost', 'not_relevant'].includes(stage);

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    background: '#0a1b36', border: '1px solid #30404e',
    borderRadius: 8, color: '#f0f2f6', fontSize: 'var(--text-input, 16px)',
    fontFamily: "'DM Sans', sans-serif", padding: '10px 12px',
    outline: 'none', marginTop: 8,
  };

  const labelStyle = {
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: 0.5, color: '#a8b8cc', marginTop: 12, display: 'block',
  };

  const slideStyle = `@keyframes slideUp { from { transform: translateY(60px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`;

  return (
    <>
      {/* ── Stepper ─────────────────────────────────────────────────────── */}
      <div style={{ marginTop: 12, marginBottom: 4 }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#6a80a0', marginBottom: 10 }}>
          Pipeline
        </div>

        {/* Main stages: Watching → Contacted → Quoted */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 8 }}>
          {STAGES.map((s, i) => {
            const isActive    = stage === s;
            const isCompleted = !isTerminal && stageIdx > i;
            const isFuture    = !isActive && !isCompleted;
            const col         = PIPELINE_COLORS[s];

            return (
              <div key={s} style={{ display: 'flex', alignItems: 'center', flex: i < STAGES.length - 1 ? 1 : 0 }}>
                {/* Dot — always clickable; tapping active shows reset */}
                <button
                  onClick={() => openSheet(s)}
                  title={isActive ? 'Tap to reset pipeline' : STAGE_LABELS[s]}
                  style={{
                    width:  isActive ? 18 : 14,
                    height: isActive ? 18 : 14,
                    borderRadius: '50%',
                    background: isCompleted || isActive ? col.dot : 'transparent',
                    border: `2px solid ${isFuture ? '#30404e' : col.dot}`,
                    cursor: 'pointer',
                    flexShrink: 0,
                    padding: 0,
                    boxShadow: isActive ? `0 0 0 3px ${col.dot}33` : 'none',
                    transition: 'all 0.15s',
                  }}
                />

                {/* Connector line */}
                {i < STAGES.length - 1 && (
                  <div style={{
                    flex: 1, height: 2,
                    background: isCompleted ? col.dot : '#30404e',
                    borderTop: isFuture ? '2px dashed #30404e' : 'none',
                    marginLeft: 2, marginRight: 2,
                    transition: 'background 0.2s',
                  }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Stage labels row */}
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          {STAGES.map((s, i) => {
            const isActive    = stage === s;
            const isCompleted = !isTerminal && stageIdx > i;
            const col         = PIPELINE_COLORS[s];
            return (
              <div key={s} style={{ flex: i < STAGES.length - 1 ? 1 : 0, minWidth: 40, textAlign: i === 0 ? 'left' : i === STAGES.length - 1 ? 'right' : 'center' }}>
                <span style={{ fontSize: 9, fontWeight: isActive ? 700 : 500, color: isActive || isCompleted ? col.text : '#6a80a0' }}>
                  {STAGE_LABELS[s]}
                </span>
              </div>
            );
          })}
        </div>

        {/* Outcome pills */}
        <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
          {['won', 'lost', 'not_relevant'].map(outcome => {
            const isActive = stage === outcome;
            const col = PIPELINE_COLORS[outcome];
            return (
              <button
                key={outcome}
                onClick={() => openSheet(outcome)}
                style={{
                  padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
                  fontSize: 11, fontWeight: 700,
                  background: isActive ? col.bg : 'transparent',
                  color: isActive ? col.text : '#6a80a0',
                  border: `1.5px solid ${isActive ? col.dot : '#30404e'}`,
                  transition: 'all 0.15s',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {outcome === 'won' ? 'Won' : outcome === 'lost' ? 'Lost' : 'Not relevant'}
              </button>
            );
          })}
        </div>

        {/* Stage timestamp */}
        {updatedAt && (
          <div style={{ fontSize: 10, color: '#6a80a0', marginTop: 6, textAlign: 'right' }}>
            {STAGE_LABELS[stage]} · {daysAgo(updatedAt)}
            {stage === 'quoted'    && quotedAmount  && ` · ${formatCost(quotedAmount)}`}
            {stage === 'won'       && wonAmount     && ` · ${formatCost(wonAmount)}`}
            {stage === 'contacted' && contactName   && ` · ${contactName}`}
            {followUpAt && !['won', 'lost', 'not_relevant'].includes(stage) && ` · follow-up ${formatFollowUp(followUpAt)}`}
          </div>
        )}
      </div>

      {/* ── Reset Confirmation Sheet ─────────────────────────────────────── */}
      {resetSheet && (
        <div
          onClick={() => setResetSheet(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'flex-end' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 540, margin: '0 auto',
              background: '#0a1b36', border: '1px solid #30404e',
              borderRadius: '16px 16px 0 0', padding: '20px 20px 32px',
              fontFamily: "'DM Sans', sans-serif",
              animation: 'slideUp 0.2s ease',
            }}
          >
            <style>{slideStyle}</style>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: '#30404e', margin: '0 auto 16px' }} />
            <div style={{ fontSize: 16, fontWeight: 700, color: '#f0f2f6', marginBottom: 4 }}>Reset pipeline?</div>
            <div style={{ fontSize: 13, color: '#a8b8cc', marginBottom: 20 }}>
              This will clear all pipeline data for this DA and return it to Watching.
            </div>
            <button
              onClick={confirmReset}
              disabled={saving}
              style={{
                width: '100%', padding: '13px 16px',
                background: 'linear-gradient(135deg, #d4a84a, #b08830)',
                border: 'none', borderRadius: 10,
                color: '#081525', fontSize: 15, fontWeight: 700,
                cursor: saving ? 'wait' : 'pointer',
                fontFamily: "'DM Sans', sans-serif",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Resetting…' : 'Reset to Watching'}
            </button>
            <button
              onClick={() => setResetSheet(false)}
              style={{
                width: '100%', marginTop: 8, padding: '13px 16px',
                background: '#0a1b36', border: '1px solid #30404e',
                borderRadius: 10, color: '#f0f2f6', fontSize: 15, fontWeight: 600,
                cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
              }}
            >Cancel</button>
          </div>
        </div>
      )}

      {/* ── Advance Stage Bottom Sheet ───────────────────────────────────── */}
      {sheet && (
        <div
          onClick={() => setSheet(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'flex-end' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 540, margin: '0 auto',
              background: '#0a1b36', border: '1px solid #30404e',
              borderRadius: '16px 16px 0 0', padding: '20px 20px 32px',
              fontFamily: "'DM Sans', sans-serif",
              animation: 'slideUp 0.2s ease',
            }}
          >
            <style>{slideStyle}</style>

            {/* Handle bar */}
            <div style={{ width: 40, height: 4, borderRadius: 2, background: '#30404e', margin: '0 auto 16px' }} />

            <div style={{ fontSize: 16, fontWeight: 700, color: '#f0f2f6', marginBottom: 4 }}>
              Move to {STAGE_LABELS[sheet.target]}
            </div>
            <div style={{ fontSize: 13, color: '#a8b8cc', marginBottom: 16 }}>
              {displayDAAddress(app, app.da_number)}
            </div>

            {/* Optional fields per stage */}
            {sheet.target === 'contacted' && (
              <>
                <label style={labelStyle}>Contact name <span style={{ color: '#6a80a0', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                <input
                  type="text"
                  value={fields.contact_name}
                  onChange={e => setFields(f => ({ ...f, contact_name: e.target.value }))}
                  placeholder="e.g. Sarah Chen"
                  style={inputStyle}
                />
                <label style={labelStyle}>Contact method <span style={{ color: '#6a80a0', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                <input
                  type="text"
                  value={fields.contact_method}
                  onChange={e => setFields(f => ({ ...f, contact_method: e.target.value }))}
                  placeholder="e.g. email, phone, in person"
                  style={inputStyle}
                />
              </>
            )}

            {sheet.target === 'quoted' && (
              <>
                <label style={labelStyle}>Quoted amount <span style={{ color: '#6a80a0', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                <input
                  type="number"
                  value={fields.quoted_amount}
                  onChange={e => setFields(f => ({ ...f, quoted_amount: e.target.value }))}
                  placeholder="e.g. 450000"
                  style={inputStyle}
                />
              </>
            )}

            {sheet.target === 'won' && (
              <>
                <label style={labelStyle}>Won amount <span style={{ color: '#6a80a0', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                <input
                  type="number"
                  value={fields.won_amount}
                  onChange={e => setFields(f => ({ ...f, won_amount: e.target.value }))}
                  placeholder={quotedAmount ? `e.g. ${quotedAmount}` : "e.g. 450000"}
                  style={inputStyle}
                />
              </>
            )}

            {sheet.target === 'lost' && (
              <>
                <label style={labelStyle}>Reason <span style={{ color: '#6a80a0', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                <select
                  value={fields.lost_reason}
                  onChange={e => setFields(f => ({ ...f, lost_reason: e.target.value }))}
                  style={{ ...inputStyle, appearance: 'none' }}
                >
                  <option value="">Select reason…</option>
                  <option value="lost to competitor">Lost to competitor</option>
                  <option value="project cancelled">Project cancelled</option>
                  <option value="budget">Budget</option>
                  <option value="timing">Timing</option>
                  <option value="other">Other</option>
                </select>
              </>
            )}

            {!['won', 'lost', 'not_relevant'].includes(sheet.target) && (
              <>
                <label style={labelStyle}>Next follow-up <span style={{ color: '#6a80a0', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                <input
                  type="date"
                  ref={followUpDateRef}
                  value={fields.next_follow_up_at}
                  onChange={e => setFields(f => ({ ...f, next_follow_up_at: e.target.value }))}
                  onInput={e => setFields(f => ({ ...f, next_follow_up_at: e.target.value }))}
                  style={inputStyle}
                />
                <label style={labelStyle}>Follow-up note <span style={{ color: '#6a80a0', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                <input
                  type="text"
                  value={fields.follow_up_reason}
                  onChange={e => setFields(f => ({ ...f, follow_up_reason: e.target.value }))}
                  placeholder="e.g. call builder after exhibition closes"
                  style={inputStyle}
                />
              </>
            )}

            <button
              onClick={confirmStage}
              disabled={saving}
              style={{
                width: '100%', marginTop: 20, padding: '13px 16px',
                background: 'linear-gradient(135deg, #d4a84a, #b08830)',
                border: 'none', borderRadius: 10,
                color: '#081525', fontSize: 15, fontWeight: 700,
                cursor: saving ? 'wait' : 'pointer',
                fontFamily: "'DM Sans', sans-serif",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Saving…' : `Confirm — ${STAGE_LABELS[sheet.target]}`}
            </button>
            <button
              onClick={() => setSheet(null)}
              style={{
                width: '100%', marginTop: 8, padding: '13px 16px',
                background: '#0a1b36', border: '1px solid #30404e',
                borderRadius: 10, color: '#f0f2f6', fontSize: 15, fontWeight: 600,
                cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Compact badge for non-expanded card summary line
export function PipelineBadge({ app }) {
  const stage = app.pipeline_stage;
  if (!stage || stage === 'watching') return null;
  const col = PIPELINE_COLORS[stage];

  function formatCostSmall(n) {
    if (!n) return null;
    if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
    return `$${Number(n).toLocaleString()}`;
  }

  const daysIn = app.pipeline_updated_at
    ? Math.floor((Date.now() - new Date(app.pipeline_updated_at)) / 86400000)
    : null;

  const valueStr =
    stage === 'won'    ? formatCostSmall(app.won_amount    || app.quoted_amount) :
    stage === 'quoted' ? formatCostSmall(app.quoted_amount) :
    null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: col.dot, flexShrink: 0, display: 'inline-block' }} />
      <span style={{ fontSize: 11, fontWeight: 600, color: col.text }}>
        {STAGE_LABELS[stage]}
        {valueStr && ` · ${valueStr}`}
        {daysIn !== null && ` · ${daysIn === 0 ? 'today' : daysIn === 1 ? '1 day ago' : `${daysIn}d ago`}`}
        {app.next_follow_up_at && !['won', 'lost', 'not_relevant'].includes(stage) && ` · ${formatFollowUp(app.next_follow_up_at)}`}
      </span>
    </div>
  );
}
